/* =========================================================================
   Theme runtime. Vanilla replacement for the source theme's Alpine stack.
   Behaviours: sticky/hiding header, announcement rotator + countdown,
   tabs, accordions, cart drawer + AJAX cart, search modal, qty steppers,
   scroll-reveal.
   ========================================================================= */
(() => {
  'use strict';

  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const money = (cents) =>
    (window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(cents)
      : (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.__CURRENCY__ || 'USD' }));

  /* ---------- header height vars + hide-on-scroll ---------- */
  function initHeader() {
    const root = document.documentElement;
    const wrap = $('.header-sections-container');
    if (!wrap) return;

    const measure = () => {
      const ann = $('[data-announcement-bar]', wrap);
      const nav = $('[data-navigation-bar]', wrap);
      if (ann) root.style.setProperty('--announcement-bar-height', ann.offsetHeight + 'px');
      if (nav) root.style.setProperty('--navigation-bar-height', nav.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });

    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      const headerH = parseInt(getComputedStyle(root).getPropertyValue('--header-height')) || 100;
      // only start hiding once we're clear of the header
      if (y > headerH) {
        root.classList.toggle('header-hidden', y > last && y - last > 4);
      } else {
        root.classList.remove('header-hidden');
      }
      root.classList.toggle('scrolled', y > 10);
      last = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
  }

  /* ---------- announcement bar: rotator + countdown ---------- */
  function initAnnouncement() {
    $$('[data-announcement-bar]').forEach((bar) => {
      const slides = $$('[data-slide]', bar);
      if (slides.length > 1) {
        const delay = parseInt(bar.dataset.rotateEvery || '5000', 10);
        let i = 0;
        slides.forEach((s, n) => { s.hidden = n !== 0; });
        setInterval(() => {
          slides[i].hidden = true;
          i = (i + 1) % slides.length;
          slides[i].hidden = false;
        }, delay);
      }

      $$('[data-countdown]', bar).forEach((el) => {
        const target = new Date(el.dataset.countdown).getTime();
        if (Number.isNaN(target)) return;
        const hEl = $('[data-cd-h]', el), mEl = $('[data-cd-m]', el), sEl = $('[data-cd-s]', el);
        const pad = (n) => String(n).padStart(2, '0');
        const tick = () => {
          let diff = Math.max(0, target - Date.now());
          const h = Math.floor(diff / 3.6e6);
          const m = Math.floor((diff % 3.6e6) / 6e4);
          const s = Math.floor((diff % 6e4) / 1000);
          if (hEl) hEl.textContent = pad(h);
          if (mEl) mEl.textContent = pad(m);
          if (sEl) sEl.textContent = pad(s);
          if (diff === 0) clearInterval(id);
        };
        tick();
        const id = setInterval(tick, 1000);
      });
    });
  }

  /* ---------- tabs ---------- */
  function initTabs() {
    $$('[data-tabs]').forEach((root) => {
      const btns   = $$('[role="tab"]', root);
      const panels = $$('[role="tabpanel"]', root);
      const select = (id) => {
        btns.forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('aria-controls') === id)));
        panels.forEach((p) => { p.hidden = p.id !== id; });
      };
      btns.forEach((b) => {
        b.addEventListener('click', () => select(b.getAttribute('aria-controls')));
        b.addEventListener('keydown', (e) => {
          const i = btns.indexOf(b);
          if (e.key === 'ArrowRight') btns[(i + 1) % btns.length].focus();
          if (e.key === 'ArrowLeft')  btns[(i - 1 + btns.length) % btns.length].focus();
        });
      });
      const initial = btns.find((b) => b.getAttribute('aria-selected') === 'true') || btns[0];
      if (initial) select(initial.getAttribute('aria-controls'));
    });
  }

  /* ---------- accordions ---------- */
  function initAccordions() {
    $$('[data-accordion] [aria-expanded]').forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      const set = (open) => {
        trigger.setAttribute('aria-expanded', String(open));
        panel.style.height = open ? panel.scrollHeight + 'px' : '0px';
        if (open) {
          panel.addEventListener('transitionend', function done() {
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', done);
          });
        }
      };
      panel.style.transition = 'height .28s cubic-bezier(.4,0,.2,1)';
      set(trigger.getAttribute('aria-expanded') === 'true');
      trigger.addEventListener('click', () => {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        if (!open) panel.style.height = '0px';
        else panel.style.height = panel.scrollHeight + 'px';
        requestAnimationFrame(() => set(!open));
      });
    });
  }

  /* ---------- overlays (drawer + modal) ---------- */
  const Overlay = {
    open(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.dataset.open = 'true';
      const bd = $('[data-overlay-backdrop]');
      if (bd) bd.dataset.open = 'true';
      document.body.classList.add('no-scroll');
      el.setAttribute('aria-hidden', 'false');
      const focusable = $('[autofocus], button, input, a[href]', el);
      focusable?.focus();
      Overlay._active = el;
    },
    close() {
      const el = Overlay._active;
      if (el) { el.dataset.open = 'false'; el.setAttribute('aria-hidden', 'true'); }
      const bd = $('[data-overlay-backdrop]');
      if (bd) bd.dataset.open = 'false';
      document.body.classList.remove('no-scroll');
      Overlay._active = null;
    }
  };

  function initOverlays() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-overlay]');
      if (opener) { e.preventDefault(); Overlay.open(opener.dataset.openOverlay); return; }
      if (e.target.closest('[data-close-overlay]') || e.target.matches('[data-overlay-backdrop]')) {
        e.preventDefault(); Overlay.close();
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Overlay.close(); });
  }

  /* ---------- AJAX cart ---------- */
  const Cart = {
    async refresh() {
      const root = window.Shopify?.routes?.root || '/';

      // The drawer's contents come from the `cart-drawer` section. Scope the swap to
      // the drawer itself — the cart PAGE also has a [data-cart-contents] node, and an
      // unscoped querySelector would overwrite the page with drawer markup.
      const drawerTarget = $('#CartDrawer [data-cart-contents]');
      if (drawerTarget) {
        try {
          const res = await fetch(`${root}?section_id=cart-drawer`);
          if (res.ok) {
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const next = $('[data-cart-contents]', doc);
            if (next) drawerTarget.innerHTML = next.innerHTML;
          }
        } catch { /* leave the drawer as-is on network failure */ }
      }

      // Refresh the cart page in place when we're on it.
      const pageTarget = $('[data-cart-page][data-cart-contents]');
      if (pageTarget) {
        try {
          const res = await fetch(`${root}cart?section_id=main-cart`);
          if (res.ok) {
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const next = $('[data-cart-page][data-cart-contents]', doc);
            if (next) pageTarget.innerHTML = next.innerHTML;
          }
        } catch { /* ignore */ }
      }

      // Cart count badge — read it from the authoritative cart.js payload.
      try {
        const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          $$('[data-cart-count]').forEach((n) => {
            n.textContent = data.item_count;
            n.hidden = data.item_count === 0;
          });
        }
      } catch { /* ignore */ }
    },

    async add(form) {
      const btn = $('[type="submit"]', form);
      btn?.setAttribute('aria-disabled', 'true');
      try {
        const root = window.Shopify?.routes?.root || '/';

        // The OhMy! buy box sets data-sampler-items="id:1,id:1,id:1" on the form when
        // the "Probier-Paket" option is picked — one pack of each flavour in a single
        // request. A plain FormData would post only the single `id` field, so the
        // shopper would silently get one pack instead of the set.
        const sampler = form.getAttribute('data-sampler-items');
        const res = sampler
          ? await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: sampler.split(',').map((pair) => {
                  const [id, qty] = pair.split(':');
                  return { id: parseInt(id, 10), quantity: parseInt(qty, 10) || 1 };
                }).filter((i) => i.id)
              })
            })
          : await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(form)
            });
        if (!res.ok) throw new Error((await res.json()).description || 'Add to cart failed');
        await Cart.refresh();
        Overlay.open('CartDrawer');
      } catch (err) {
        const slot = $('[data-form-error]', form);
        if (slot) { slot.textContent = err.message; slot.hidden = false; }
      } finally {
        btn?.removeAttribute('aria-disabled');
      }
    },
    async change(line, quantity) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (res.ok) await Cart.refresh();
    }
  };

  function initCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      Cart.add(form);
    });
    document.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (step) {
        const input = $('input', step.closest('.qty'));
        const delta = step.dataset.qtyStep === 'up' ? 1 : -1;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) { e.preventDefault(); Cart.change(parseInt(remove.dataset.cartRemove, 10), 0); }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      Cart.change(parseInt(input.dataset.cartQty, 10), Math.max(0, parseInt(input.value, 10) || 0));
    });
  }

  /* ---------- PDP plan selector ----------
     The pack cards are radios; the cart only understands `quantity`, so mirror
     the chosen pack onto the form's quantity input, creating it when the
     quantity block is not on the page. */
  function initPlans() {
    $$('[data-plans]').forEach((group) => {
      const form = group.closest('form');
      if (!form) return;
      const sync = () => {
        const picked = $('input[name="pack"]:checked', group);
        if (!picked) return;
        let qty = form.querySelector('input[name="quantity"]');
        if (!qty) {
          qty = document.createElement('input');
          qty.type = 'hidden';
          qty.name = 'quantity';
          form.appendChild(qty);
        }
        qty.value = picked.dataset.planQty || '1';
      };
      group.addEventListener('change', sync);
      sync();
    });
  }

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    const els = $$('[data-reveal]');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- boot ---------- */
  const boot = () => {
    initHeader(); initAnnouncement(); initTabs(); initAccordions();
    initOverlays(); initCart(); initPlans(); initReveal();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', boot);
  window.ThemeCart = Cart;
  window.ThemeOverlay = Overlay;
})();
